import React from 'react';
import SEO from '../components/SEO';
import { businessConfig as BusinessInfo } from '../config/businessConfig';

const PrivacyPolicy = () => {
  return (
    <div className="bg-white min-h-screen py-24 px-6 selection:bg-stone-100">
      <SEO 
        title={`Privacy & Data Protocol | ${BusinessInfo.name}`} 
        description={`Architectural confidentiality and data protection protocols for ${BusinessInfo.name}.`} 
      />
      
      <div className="max-w-3xl mx-auto">
        <header className="mb-16 border-b border-stone-100 pb-8">
          <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-4 italic">
            Privacy & <span className="not-italic font-light text-stone-500">Data Protocol.</span>
          </h1>
          <p className="text-stone-400 text-[10px] uppercase tracking-[0.3em] font-bold">
            Version 2.2 • {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </header>

        <div className="space-y-12">
          {/* Section 1: Capture */}
          <section>
            <h2 className="text-sm font-bold text-stone-900 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-700"></span>
              1. Information Capture
            </h2>
            <p className="text-stone-600 leading-relaxed font-light mb-4 text-sm">
              To provide high-fidelity technical designs and independent project oversight, we collect data through our proprietary analysis engines. This process is focused on technical logic and infrastructure integrity, including:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-stone-600 font-light list-none">
              <li className="flex items-center gap-2 text-[13px]">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-600" /> Client Portfolio Credentials
              </li>
              <li className="flex items-center gap-2 text-[13px]">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-600" /> Infrastructure Metadata
              </li>
              <li className="flex items-center gap-2 text-[13px]">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-600" /> System Behavioral Analytics
              </li>
              <li className="flex items-center gap-2 text-[13px]">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-600" /> Architectural Project Scope
              </li>
            </ul>
          </section>

          {/* Section 2: Utility */}
          <section>
            <h2 className="text-sm font-bold text-stone-900 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-700"></span>
              2. Data Utility & Directives
            </h2>
            <p className="text-stone-600 leading-relaxed font-light mb-4 text-sm">
              Your information is utilized solely to maintain the technical integrity of your project's logic and design. Our data application includes:
            </p>
            <ul className="space-y-4 text-stone-600 font-light text-sm">
              <li className="border-l-2 border-amber-600/20 pl-4 italic">Drafting comprehensive Technical Directives for project implementation.</li>
              <li className="border-l-2 border-amber-600/20 pl-4 italic">Performing forensic audits and logic-based troubleshooting.</li>
              <li className="border-l-2 border-amber-600/20 pl-4 italic">Providing administrative oversight for third-party infrastructure teams.</li>
            </ul>
          </section>

          {/* Section 3: Framework */}
          <section>
            <h2 className="text-sm font-bold text-stone-900 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-700"></span>
              3. Strategic Framework
            </h2>
            <p className="text-stone-600 leading-relaxed font-light mb-4 text-sm">
              We operate exclusively as a technical design and consultation firm. We do not monetize data through third-party marketing channels.
            </p>
            <div className="bg-stone-50 border border-stone-100 p-8 rounded-[2rem] text-stone-500 font-light text-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <p className="relative z-10 leading-relaxed">
                <strong className="text-stone-900 block mb-2 uppercase tracking-tighter text-[10px]">Strategic Disclosure:</strong>
                Specific architectural blueprints and technical requirements are shared strictly with vetted implementation partners. This ensures that the physical execution aligns perfectly with our proprietary design specs, permitting requirements, and safety standards.
              </p>
            </div>
          </section>

          {/* Section 4: Contact */}
          <section className="pt-12 border-t border-stone-100">
            <h2 className="text-sm font-bold text-stone-900 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-700"></span>
              4. Contact Administration
            </h2>
            <div className="bg-stone-900 text-white p-10 rounded-[2.5rem] shadow-2xl border border-white/5">
              <p className="font-serif italic text-white text-2xl mb-2">{BusinessInfo.name}</p>
              <p className="text-stone-400 font-light text-[9px] uppercase tracking-[0.3em] mb-8">Strategic Infrastructure & Forensic Consulting</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm tracking-wide">
                <div>
                  <p className="text-stone-500 uppercase text-[9px] font-bold mb-1">Regional HQ</p>
                  <p className="font-light">Huntersville / Charlotte, NC</p>
                </div>
                <div>
                  <p className="text-stone-500 uppercase text-[9px] font-bold mb-1">Protocol Access</p>
                  <p className="text-amber-500 font-bold tracking-widest">{BusinessInfo.phone}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;